import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFetch from "../../../../hooks/useFetch";

const today = new Date().toISOString().split("T")[0];

const ScorePlusPharma = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const pharmaCode = authState?.user?.think5whyscode || authState?.think5whyscode || "";
  const token = authState?.token;
  const [fetchData] = useFetch();
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "latestDate", direction: "desc" });

  // Max 31 days validation
  const isValidRange = useMemo(() => {
    if (!fromDate || !toDate) return false;
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diff = (to - from) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 31;
  }, [fromDate, toDate]);

  const loadDoctors = async () => {
    if (!pharmaCode || !isValidRange) return;

    try {
      setLoading(true);
      setMessage(null);

      const config = {
        url: "/scoreplus/pharma-doctors",
        method: "post",
        data: {
          viewerCode2: pharmaCode,
          fromDate,
          toDate,
        },
        headers: {
          Authorization: token,
        },
      };

      const res = await fetchData(config, { silent: true });

      if (res.success) {
        setDoctors(res.doctors || []);
      } else {
        setMessage(res.message || "Unable to load doctors");
        setDoctors([]);
      }
    } catch (err) {
      console.error(err);
      setMessage("Unable to load doctors");
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pharmaCode) {
      loadDoctors();
    }
  }, [pharmaCode]);

  const handleSearch = () => {
    if (!isValidRange) {
      setMessage("Date range cannot exceed 31 days and From must be ≤ To");
      return;
    }
    loadDoctors();
  };

  const handleClear = () => {
    setFromDate(today);
    setToDate(today);
    setMessage(null);
  };

  // Sorting
  const sortedDoctors = useMemo(() => {
    const sorted = [...doctors];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "latestDate") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else {
        aVal = String(aVal || "").toLowerCase();
        bVal = String(bVal || "").toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [doctors, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <span className="text-zinc-500">↕</span>;
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  if (!pharmaCode) {
    return (
      <div className="p-6 text-center text-red-400">
        Doctor code not found. Please login again.
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-yellow-200 flex items-center gap-2">
          🩺 ScorePlus – Pharma Company View
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          Doctors linked to your code: <span className="text-cyan-400">{pharmaCode}</span>
        </p>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-zinc-800 text-white rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-zinc-800 text-white rounded px-3 py-1.5 text-sm"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !isValidRange}
            className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-zinc-700 text-white px-4 py-1.5 rounded text-sm font-medium"
          >
            {loading ? "Loading..." : "🔍 Search"}
          </button>
          <button
            onClick={handleClear}
            className="text-zinc-400 hover:text-white text-sm underline"
          >
            Reset to Today
          </button>
        </div>
        {!isValidRange && (
          <p className="text-xs text-amber-400 mt-2">
            ⚠️ Date range must be between 0–31 days
          </p>
        )}
        {message && (
          <p className="text-xs text-red-400 mt-2">{message}</p>
        )}
      </div>

      {/* Results count */}
      <div className="mb-3 text-sm text-zinc-400">
        {doctors.length} doctors{doctors.length !== 1 ? "s" : ""} found
      </div>

      {/* Empty State */}
      {!loading && doctors.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-yellow-500/20 bg-yellow-500/5 px-4 py-12 text-center">
          <div className="mb-3 text-5xl">🔍</div>
          <h3 className="text-sm font-semibold text-yellow-300">No Doctors Found</h3>
          <p className="mt-2 text-xs text-zinc-400">
            No activity records linked to your code in the selected date range.
          </p>
        </div>
      )}

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {sortedDoctors.map((p) => (
          <div
            key={p.userCode}
            onClick={() => navigate(`/scoreplus-doctor/${p.userCode}`)}
            className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 cursor-pointer hover:border-cyan-600 transition"
          >
            <div className="flex justify-between items-start mb-2">
              <div className="font-semibold text-white">{p.userCode}</div>
              <span className="text-xs bg-cyan-900/50 text-cyan-300 px-2 py-0.5 rounded-full">
                {p.totalRecords} records
              </span>
            </div>
            <div className="text-sm text-zinc-400">
              📅 {new Date(p.latestDate).toLocaleDateString()}
            </div>
            <div className="text-sm text-white mt-1 truncate">{p.latestActivity || p.latestAct}</div>
            <div className="mt-2">
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  p.latestStatus === "Done"
                    ? "bg-green-900 text-green-300"
                    : p.latestStatus === "Partial"
                    ? "bg-yellow-900 text-yellow-300"
                    : "bg-red-900 text-red-300"
                }`}
              >
                {p.latestStatus}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-zinc-900 border border-zinc-700 rounded-xl overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-800">
            <tr>
              <th
                className="p-3 text-left cursor-pointer hover:text-cyan-400"
                onClick={() => requestSort("userCode")}
              >
                Code <SortIcon column="userCode" />
              </th>
              <th
                className="p-3 text-left cursor-pointer hover:text-cyan-400"
                onClick={() => requestSort("latestDate")}
              >
                Latest Date <SortIcon column="latestDate" />
              </th>
              <th
                className="p-3 text-left cursor-pointer hover:text-cyan-400"
                onClick={() => requestSort("latestActivity")}
              >
                Activity <SortIcon column="latestActivity" />
              </th>
              <th
                className="p-3 text-left cursor-pointer hover:text-cyan-400"
                onClick={() => requestSort("latestStatus")}
              >
                Status <SortIcon column="latestStatus" />
              </th>
              <th className="p-3 text-center">Records</th>
            </tr>
          </thead>
          <tbody>
            {sortedDoctors.map((p) => (
              <tr
                key={p.userCode}
                onClick={() => navigate(`/scoreplus-doctor/${p.userCode}`)}
                className="border-t border-zinc-800 hover:bg-zinc-800/60 cursor-pointer"
              >
                <td className="p-3 font-medium text-cyan-300">{p.userCode}</td>
                <td className="p-3">{new Date(p.latestDate).toLocaleDateString()}</td>
                <td className="p-3">{p.latestActivity || p.latestAct}</td>
                <td className="p-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      p.latestStatus === "Done"
                        ? "bg-green-900 text-green-300"
                        : p.latestStatus === "Partial"
                        ? "bg-yellow-900 text-yellow-300"
                        : "bg-red-900 text-red-300"
                    }`}
                  >
                    {p.latestStatus}
                  </span>
                </td>
                <td className="p-3 text-center">
                  <span className="text-xs bg-zinc-700 text-zinc-200 px-2 py-0.5 rounded-full">
                    {p.totalRecords}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScorePlusPharma;